'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff, CheckCircle, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { authClient } from '@/lib/auth/client';
import { loginSchema, type LoginInput } from '../types/schemas';

interface LoginFormProps {
  searchParams: {
    success?: string;
    email?: string;
    returnTo?: string;
  };
}

export function LoginForm({ searchParams }: LoginFormProps) {
  const { success, email: emailParam, returnTo } = searchParams;

  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  // Show success toast for password-reset on mount
  useEffect(() => {
    if (success === 'password-reset') {
      toast.success(
        'Password updated successfully. Please sign in with your new password.',
      );
    }
  }, [success]);

  const form = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: emailParam ?? '',
      password: '',
    },
  });

  const showBanner =
    !bannerDismissed &&
    (success === 'verified' || success === 'invitation-accepted');

  const bannerMessage =
    success === 'verified'
      ? '✓ Email verified — your account is now active. Please sign in.'
      : '✓ Account created — welcome to ImpactOT. Please sign in to get started.';

  async function onSubmit(values: LoginInput) {
    setFormError(null);
    setIsPending(true);
    try {
      const result = await authClient.signIn.email({
        email: values.email,
        password: values.password,
        callbackURL: returnTo ?? '/assessments',
      });

      if (result.error) {
        const code = result.error.code ?? '';
        const message = result.error.message ?? '';

        if (
          code === 'EMAIL_NOT_VERIFIED' ||
          message.toLowerCase().includes('not verified') ||
          message.toLowerCase().includes('verify')
        ) {
          setFormError('EMAIL_NOT_VERIFIED');
        } else if (
          code === 'USER_BANNED' ||
          message.toLowerCase().includes('ban') ||
          message.toLowerCase().includes('deactivated')
        ) {
          setFormError('USER_BANNED');
        } else {
          setFormError('INVALID_CREDENTIALS');
        }
        // Clear password on error
        form.setValue('password', '');
      }
    } catch {
      setFormError('INVALID_CREDENTIALS');
      form.setValue('password', '');
    } finally {
      setIsPending(false);
    }
  }

  return (
    <div className="space-y-5">
      {/* Success banner for verified / invitation-accepted */}
      {showBanner && (
        <div className="flex items-start gap-3 rounded-md border border-teal-200 bg-teal-50 px-4 py-3 text-teal-800">
          <CheckCircle className="h-4 w-4 mt-0.5 shrink-0 text-teal-600" />
          <p className="text-sm font-sans flex-1">{bannerMessage}</p>
          <button
            type="button"
            onClick={() => setBannerDismissed(true)}
            className="text-teal-600 hover:text-teal-800 transition-colors shrink-0"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <Form {...form}>
        {/* Instructional text */}
        <p className="text-sm font-sans text-muted-foreground">
          Don&apos;t have an account?{' '}
          <Link
            href="/register"
            className="text-foreground font-medium hover:underline transition-colors"
          >
            Sign up
          </Link>{' '}
          or request access from your organisation administrator.
        </p>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 mt-4">
          {/* Email */}
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-sans text-sm font-medium text-foreground">
                  Email address
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="email"
                    placeholder="you@company.com"
                    disabled={isPending}
                    className="font-sans"
                  />
                </FormControl>
                <FormMessage className="text-destructive text-xs font-sans" />
              </FormItem>
            )}
          />

          {/* Password */}
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center justify-between">
                  <FormLabel className="font-sans text-sm font-medium text-foreground">
                    Password
                  </FormLabel>
                  <Link
                    href="/forgot-password"
                    className="text-xs font-sans text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Forgot your password?
                  </Link>
                </div>
                <FormControl>
                  <div className="relative">
                    <Input
                      {...field}
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      disabled={isPending}
                      className="font-sans pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      tabIndex={-1}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </FormControl>
                <FormMessage className="text-destructive text-xs font-sans" />
              </FormItem>
            )}
          />

          {/* Form-level error — below fields, above button */}
          {formError && (
            <div
              className={cn(
                'rounded-md border px-4 py-3 text-sm font-sans',
                'bg-destructive/5 border-destructive/20 text-destructive',
              )}
            >
              {formError === 'INVALID_CREDENTIALS' && (
                <p>
                  The email or password you entered is incorrect. Please try
                  again.
                </p>
              )}
              {formError === 'EMAIL_NOT_VERIFIED' && (
                <p>
                  Please verify your email address before signing in.{' '}
                  <Link
                    href="/verify-email"
                    className="underline font-medium hover:no-underline"
                  >
                    Resend verification email →
                  </Link>
                </p>
              )}
              {formError === 'USER_BANNED' && (
                <p>
                  This account has been deactivated. Contact your organisation
                  administrator.
                </p>
              )}
            </div>
          )}

          {/* Submit */}
          <Button
            type="submit"
            disabled={isPending}
            className="w-full bg-accent text-accent-foreground hover:bg-accent/90 hover-glow-amber font-sans font-medium transition-all"
          >
            {isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Signing in…
              </>
            ) : (
              'Sign in'
            )}
          </Button>
        </form>
      </Form>
    </div>
  );
}
